import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import {
  ArrowRight,
  Lock,
  Map,
  Rocket,
  Ticket,
  Users,
  Workflow,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import {
  absoluteSigninUrl,
  absoluteSignupUrl,
  resolveHostMode,
} from '../lib/hostMode'

const STAGES = [
  'Discover',
  'Prioritize',
  'LOE',
  'Build',
  'Test',
  'QA',
  'Release',
] as const

const JOURNEY = [
  {
    title: 'Idea, ticket, or interview',
    body: 'Product ideas, Jira/Linear, Zendesk — or a creator interview.',
  },
  {
    title: 'Prioritize & estimate',
    body: 'RICE scoring plus AI delivery-team LOE and roadmaps.',
  },
  {
    title: 'Autonomous teams build',
    body: 'Specialist agent teams design, build, test, and audit with reports.',
  },
  {
    title: 'Ephemeral co-build',
    body: 'Product, eng, and AI verify together in a disposable preview.',
  },
  {
    title: 'You approve',
    body: 'Sign off the POC. Merge and publish stay human — never silent.',
  },
] as const

export function LandingPage() {
  const reduce = useReducedMotion()
  const mode = resolveHostMode()
  const signupHref = absoluteSignupUrl(mode, 'com')
  const signinHref = absoluteSigninUrl(mode, 'com')
  const externalApp = signupHref.startsWith('http')

  return (
    <AppChrome marketing>
      {/* Hero — one composition: brand, headline, sentence, CTAs, full-bleed visual */}
      <section className="relative z-10 flex min-h-[calc(100svh-4.5rem)] flex-col justify-end overflow-hidden px-4 pb-16 pt-10 sm:px-6 sm:pb-20 lg:justify-center lg:pb-24">
        <div className="pointer-events-none absolute inset-0 -z-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_35%,rgba(46,230,197,0.16),transparent_55%)]" />
          <motion.div
            className="hero-orbit absolute -right-[20%] top-[8%] h-[70vmin] w-[70vmin] rounded-full border border-[rgba(46,230,197,0.22)]"
            animate={reduce ? undefined : { rotate: 360 }}
            transition={{ duration: 80, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="hero-orbit absolute -right-[8%] top-[18%] h-[48vmin] w-[48vmin] rounded-full border border-[rgba(255,179,71,0.18)]"
            animate={reduce ? undefined : { rotate: -360 }}
            transition={{ duration: 55, repeat: Infinity, ease: 'linear' }}
          />
          <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[var(--color-void)] to-transparent" />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            <p className="font-[family-name:var(--font-display)] text-[clamp(3.4rem,14vw,8.5rem)] font-extrabold leading-[0.86] tracking-[-0.04em]">
              <span className="text-gradient">AFOS 360</span>
            </p>
            <h1 className="mt-5 max-w-3xl font-[family-name:var(--font-display)] text-[clamp(1.65rem,4.5vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--color-foam)]">
              Product and engineering on one factory OS.
            </h1>
            <p className="mt-4 max-w-xl text-base text-[var(--color-mist)] sm:text-lg">
              From idea and ticket to AI team LOE, autonomous build, and ephemeral
              co-verify — then your people approve. Multi-tenant. Multi-cloud.
              Human-gated by design.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              {externalApp ? (
                <a
                  href={signupHref}
                  className="neon-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[var(--color-foam)] px-7 py-3 text-base font-semibold text-[var(--color-void)]"
                >
                  Start free trial
                  <ArrowRight className="h-4 w-4" />
                </a>
              ) : (
                <Link
                  to={signupHref}
                  className="neon-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[var(--color-foam)] px-7 py-3 text-base font-semibold text-[var(--color-void)]"
                >
                  Start free trial
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
              {externalApp ? (
                <a
                  href={signinHref}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[var(--color-line)] px-7 py-3 text-base text-[var(--color-foam)]"
                >
                  Sign in
                </a>
              ) : (
                <Link
                  to={signinHref}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[var(--color-line)] px-7 py-3 text-base text-[var(--color-foam)]"
                >
                  Sign in
                </Link>
              )}
              <a
                href="#security"
                className="inline-flex min-h-12 items-center justify-center px-2 text-sm text-[var(--color-mist)] underline-offset-2 hover:underline"
              >
                Security & gates
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product proof — one job */}
      <section
        id="features"
        className="relative z-10 border-t border-[var(--color-line)] px-4 py-20 sm:px-6"
      >
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.45 }}
          >
            <h2 className="font-[family-name:var(--font-display)] text-3xl font-extrabold tracking-tight sm:text-5xl">
              Beyond chatbots. Beyond ticket limbo.
            </h2>
            <p className="mt-3 max-w-2xl text-[var(--color-mist)] sm:text-lg">
              A governed product lifecycle and agile SDLC — PM dashboards, agent
              skills teams, Jira/Linear sync, and human merge gates in one OS.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-10 md:grid-cols-2 lg:grid-cols-3">
            <FeatureBlock
              icon={<Map className="h-5 w-5 text-[var(--color-cyan)]" />}
              title="Product lifecycle"
              body="Ideas, insights, RICE, roadmaps, and delivery/QA status — JPD-class discovery with AI team LOE estimates."
            />
            <FeatureBlock
              icon={<Ticket className="h-5 w-5 text-[var(--color-cyan)]" />}
              title="Jira + Linear SDLC"
              body="Sync delivery work into AFOS; draft issues for human publish. Zendesk and Freshdesk stay first-class too."
            />
            <FeatureBlock
              icon={<Workflow className="h-5 w-5 text-[var(--color-cyan)]" />}
              title="Agent skills teams"
              body="Every tenant boots Discovery, Delivery Spine, Quality Gate, Security, and more — peer-qualify before merge talk."
            />
            <FeatureBlock
              icon={<Users className="h-5 w-5 text-[var(--color-amber)]" />}
              title="Ephemeral co-build"
              body="Product, eng, and AI share a disposable preview: checklist, chat, sign-off — then you merge."
            />
            <FeatureBlock
              icon={<Rocket className="h-5 w-5 text-[var(--color-amber)]" />}
              title="Software shop OS"
              body="New software, enhance existing apps, bug-fix legacy clients, and Zendesk CSAT support agents — one complete SDLC."
            />
            <FeatureBlock
              icon={<Lock className="h-5 w-5 text-[var(--color-rose)]" />}
              title="Enterprise trust"
              body="PM / eng / QA roles, CUST_/PROJ_ isolation, Cognito/Google SSO, OTP, secret-safe keys — no silent prod."
            />
          </div>
        </div>
      </section>

      {/* Customer journey — one job */}
      <section className="relative z-10 border-t border-[var(--color-line)] px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-[family-name:var(--font-display)] text-3xl font-extrabold tracking-tight sm:text-5xl">
            One path from need to POC.
          </h2>
          <p className="mt-3 max-w-2xl text-[var(--color-mist)] sm:text-lg">
            Whether work starts as a product idea, a Jira issue, or a creator
            interview — the same governed spine takes you to a reviewable preview.
          </p>
          <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {JOURNEY.map((step, i) => (
              <motion.li
                key={step.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="rounded-3xl border border-[var(--color-line)] bg-black/25 px-4 py-5"
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--color-mist)]">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="mt-2 font-[family-name:var(--font-display)] text-lg font-bold text-[var(--color-foam)]">
                  {step.title}
                </div>
                <p className="mt-2 text-sm text-[var(--color-mist)]">{step.body}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* Lifecycle stages — one job */}
      <section
        id="workforce"
        className="relative z-10 border-t border-[var(--color-line)] px-4 py-20 sm:px-6"
      >
        <div className="mx-auto max-w-6xl">
          <h2 className="font-[family-name:var(--font-display)] text-3xl font-extrabold tracking-tight sm:text-5xl">
            Product meets delivery.
          </h2>
          <p className="mt-3 max-w-2xl text-[var(--color-mist)] sm:text-lg">
            Prioritize with RICE, estimate with AI delivery teams, then run the
            autonomous build and QA loop — ending in an ephemeral workspace, not a
            slide deck.
          </p>
          <div className="mt-10 flex gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {STAGES.map((stage, i) => (
              <motion.div
                key={stage}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="min-w-[9.5rem] shrink-0 rounded-3xl border border-[var(--color-line)] bg-black/25 px-4 py-5"
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--color-mist)]">
                  Stage {i + 1}
                </div>
                <div className="mt-2 font-[family-name:var(--font-display)] text-xl font-bold text-[var(--color-foam)]">
                  {stage}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security / trust — market penetration proof */}
      <section
        id="security"
        className="relative z-10 border-t border-[var(--color-line)] px-4 py-20 sm:px-6"
      >
        <div className="mx-auto max-w-6xl">
          <h2 className="font-[family-name:var(--font-display)] text-3xl font-extrabold tracking-tight sm:text-5xl">
            Human-gated by design.
          </h2>
          <p className="mt-3 max-w-2xl text-[var(--color-mist)] sm:text-lg">
            SSO in production, SendGrid OTP, hash-bound Jira/Linear publish, CVE
            Critical/High block merge unless named-human ACCEPT_CVE_RISK — never
            silent Zendesk or PROD writes.
          </p>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {[
              {
                t: 'Identity',
                b: 'Cognito / Google SSO · demo labeled · SendGrid step-up OTP',
              },
              {
                t: 'Publish gates',
                b: 'PUBLISH_ZENDESK_REPLY · PUBLISH_ISSUE_DRAFT + payload hash',
              },
              {
                t: 'Ship gates',
                b: 'Ephemeral apply ≠ PROD · APPROVE_MERGE · platform CVE PASS',
              },
            ].map((row) => (
              <div
                key={row.t}
                className="rounded-3xl border border-[var(--color-line)] bg-black/25 px-5 py-6"
              >
                <div className="font-[family-name:var(--font-display)] text-lg font-bold text-[var(--color-foam)]">
                  {row.t}
                </div>
                <p className="mt-2 text-sm text-[var(--color-mist)]">{row.b}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-[var(--color-mist)]">
            Product runs on{' '}
            <span className="text-[var(--color-foam)]">afos360.ai</span>
            {' '}· this site ({' '}
            <span className="text-[var(--color-foam)]">afos360.com</span>
            ) earns trust and pipeline.
          </p>
        </div>
      </section>

      {/* Close CTA — signup interaction */}
      <section
        id="signup"
        className="relative z-10 border-t border-[var(--color-line)] px-4 py-20 sm:px-6"
      >
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <h2 className="font-[family-name:var(--font-display)] text-3xl font-extrabold tracking-tight sm:text-5xl">
              Spin up a product factory in minutes.
            </h2>
            <p className="mt-3 max-w-xl text-[var(--color-mist)] sm:text-lg">
              Sign up, capture an idea or pull Jira/Linear, get AI LOE, launch
              agent teams, and verify with your engineers in an ephemeral preview —
              before anyone merges to main.
            </p>
          </div>
          <div className="glass neon-ring rounded-[2rem] p-6 sm:p-8">
            <div className="flex items-center gap-2 text-[var(--color-cyan)]">
              <Rocket className="h-5 w-5" />
              <span className="text-sm font-semibold uppercase tracking-[0.18em]">
                Free trial
              </span>
            </div>
            <p className="mt-3 font-[family-name:var(--font-display)] text-2xl font-bold">
              Start building today
            </p>
            <p className="mt-2 text-sm text-[var(--color-mist)]">
              Google SSO when configured — or continue in demo mode to explore the
              full product + engineering factory.
            </p>
            {externalApp ? (
              <a
                href={signupHref}
                className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--color-cyan)] px-6 py-3 font-semibold text-[var(--color-void)]"
              >
                Create your workspace
                <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <Link
                to={signupHref}
                className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--color-cyan)] px-6 py-3 font-semibold text-[var(--color-void)]"
              >
                Create your workspace
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <p className="mt-3 text-center text-xs text-[var(--color-mist)]">
              Already have access?{' '}
              {externalApp ? (
                <a
                  href={signinHref}
                  className="text-[var(--color-foam)] underline-offset-2 hover:underline"
                >
                  Sign in on app.afos360.ai
                </a>
              ) : (
                <Link
                  to={signinHref}
                  className="text-[var(--color-foam)] underline-offset-2 hover:underline"
                >
                  Sign in
                </Link>
              )}
            </p>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-[var(--color-line)] px-4 py-8 text-sm text-[var(--color-mist)] sm:px-6">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-[family-name:var(--font-display)] font-semibold text-[var(--color-foam)]">
            AFOS 360
          </span>
          <span className="flex flex-wrap gap-x-4 gap-y-1">
            <span>afos360.com · market</span>
            <span>afos360.ai · factory</span>
            <span>Human-gated by design</span>
          </span>
        </div>
      </footer>
    </AppChrome>
  )
}

function FeatureBlock({
  icon,
  title,
  body,
}: {
  icon: ReactNode
  title: string
  body: string
}) {
  return (
    <div>
      <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-[var(--color-line)] bg-black/30">
        {icon}
      </div>
      <h3 className="font-[family-name:var(--font-display)] text-xl font-bold">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-[var(--color-mist)] sm:text-base">
        {body}
      </p>
    </div>
  )
}
